import React from 'react';
import { ComposedChart, XAxis, YAxis, ResponsiveContainer, Bar, Cell, ReferenceLine } from 'recharts';
import { endOfDay, format, startOfDay, startOfHour } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { OmaLummeApp } from '../../omaLummeContext';

import styles from './styles/NordPoolSpotGraph.module.scss';

import Info from '../Common/Info';
import Loading from '../Common/Loading';
import Snackbar from '../Common/Snackbar';
import Switch from '../Common/Switch';

// Actions.
import { fetchNordPoolSpotData } from '../../actions';
// Types.
import { NordPoolSpotGraphDatapoint } from '../../types/graphs';
// Utils.
import { getDateFormat } from '../../utils/consumption';
import { useWindowSize } from '../Layout/Header';

const ConsumptionImpactGraph: React.FunctionComponent = () => {
  const { config, selectedMeteringPointContract } = OmaLummeApp.useState();

  const [rawTodayData, setRawTodayData] = React.useState<NordPoolSpotGraphDatapoint[]>();
  const [rawTomorrowData, setRawTomorrowData] = React.useState<NordPoolSpotGraphDatapoint[]>();
  const [nordPoolData, setNordPoolData] = React.useState<NordPoolSpotGraphDatapoint[]>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [activeTab, setActiveTab] = React.useState<boolean>(false);
  const [dataLoadingError, setDataLoadingError] = React.useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const FinnishTime = "yyyy-MM-dd'T'HH:mm:ss'Z'";

  const windowWidth = useWindowSize()[0];

  React.useEffect(() => {
    let unMounted = false;
    // Create an scoped async function in the hook
    async function fetchGraphPoints(): Promise<void> {
      setLoading(true);

      const data = await fetchNordPoolSpotData(format(startOfDay(new Date()), FinnishTime), format(endOfDay(new Date()), FinnishTime), 'hour', true);

      if (!unMounted && data !== false) {
        const today = data.slice(0, 24);
        const tomorrow = data.slice(24, 48);

        setRawTodayData(today);
        if (tomorrow.length === 24) {
          setRawTomorrowData(tomorrow);
        } else {
          setRawTomorrowData(undefined);
        }
      }

      if (data === false) {
        setDataLoadingError(true);
        setLoading(false);
      }
    }

    if (!unMounted) {
      if (!rawTodayData) {
        fetchGraphPoints();
      }
    }

    return (): void => {
      unMounted = true;
    };
  }, []);

  React.useEffect(() => {
    const formatData = (data: NordPoolSpotGraphDatapoint[]) => {
      return data.map((point) => {
        const pointObj: NordPoolSpotGraphDatapoint = {
          meanPriceWithVat: point.meanPriceWithVat / 10,
          meanPriceWithoutVat: point.meanPriceWithoutVat / 10,
          period: point.period,
          priceLevel: point.priceLevel,
          vatRate: point.vatRate,
          deliveryArea: point.deliveryArea,
          unit: point.unit,
        };

        return pointObj;
      });
    };
    if (activeTab && rawTomorrowData && rawTomorrowData.length > 0) {
      setNordPoolData(formatData(rawTomorrowData));
    } else if (rawTodayData && rawTodayData.length > 0) {
      setNordPoolData(formatData(rawTodayData));
    } else {
      setNordPoolData(undefined);
    }
  }, [rawTodayData, rawTomorrowData, activeTab]);

  React.useEffect(() => {
    if (nordPoolData && config && selectedMeteringPointContract && config.consumptionImpact === true && selectedMeteringPointContract.isFlexible) {
      setLoading(false);
    }
    if (config && selectedMeteringPointContract && !(config.consumptionImpact === true && selectedMeteringPointContract.isFlexible)) {
      navigate('/');
    }
  }, [config, selectedMeteringPointContract, nordPoolData]);

  function getRanges(array: number[]) {
    const ranges: string[] = [];
    let rstart: number;
    let rend: number;
    for (let i = 0; i < array.length; i++) {
      rstart = array[i];
      rend = rstart + 1;
      while (array[i + 1] - array[i] === 1) {
        rend = array[i + 1] + 1;
        i++;
      }
      const start = rstart.toString().padStart(2, '0');
      const end = rend.toString().padStart(2, '0');
      ranges.push(`${start}–${end}`);
    }
    return ranges;
  }

  function getHours(priceLevel: string) {
    return nordPoolData
      ? Array.from(
          nordPoolData.filter((d) => d.priceLevel === priceLevel),
          (d) => parseInt(format(d.period.identifier, 'H')),
        )
      : [];
  }

  const currentHour = startOfHour(new Date());

  const customTick = (tickObject: any) => {
    const {
      payload: { value },
    } = tickObject;

    const tickValue = format(new Date(value), getDateFormat('hour', true));

    const activeHour = !activeTab && format(currentHour, 'HH') === tickValue;

    return (
      <foreignObject x={tickObject.x - tickObject.height / 2} y={tickObject.y} width={tickObject.height} height={tickObject.height}>
        <div className={`${styles.tick} ${activeHour ? styles.activeTick : undefined}`}>{tickValue}</div>
      </foreignObject>
    );
  };

  return (
    <div className={`graph-container graph-flex ${styles.nordPoolGraph}`}>
      <h1 className={styles.title}>{t('consumption:consumptionImpactGraph.title')}</h1>
      {rawTomorrowData && (
        <div className={styles.switch}>
          <Switch
            offLabel={t(`common:general.today`)}
            onLabel={t(`common:general.tomorrow`)}
            active={activeTab}
            onChange={() => setActiveTab(!activeTab)}
          />
        </div>
      )}
      {loading && <Loading />}
      {!loading && dataLoadingError && <Snackbar type="error">{t('consumption:consumptionImpactGraph.errorLoadingData')}</Snackbar>}
      {nordPoolData && !loading && (
        <div className="graph-wrapper consumptionImpactGraph">
          <ResponsiveContainer width={'100%'} height={300}>
            <ComposedChart data={nordPoolData} margin={{ top: 25, right: 0, bottom: 0, left: 0 }}>
              <XAxis dataKey="period.start" stroke="#7B7B7B" interval={windowWidth >= 768 ? 0 : 1} tick={(tickObject) => customTick(tickObject)} />
              <YAxis hide={true} scale="linear" interval="preserveStartEnd" strokeWidth={0} />
              {nordPoolData && nordPoolData.length > 0 && (
                <>
                  <Bar name={`${t(`contracts:contracts.spotPrice`)}`} dataKey={'meanPriceWithVat'} radius={[50, 50, 0, 0]}>
                    {nordPoolData.map((entry, index) => {
                      let fill = '#788E9D';

                      if (nordPoolData[index]['priceLevel'] === 'LOW') {
                        fill = '#c0e300';
                      }
                      if (nordPoolData[index]['priceLevel'] === 'HIGH') {
                        fill = '#AB33A5';
                      }

                      return <Cell fill={fill} key={`nordpool-fill-${index}`} />;
                    })}
                  </Bar>
                  <ReferenceLine x={format(currentHour, FinnishTime)} stroke="#96008f" strokeWidth={2} />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <ul className={styles.impactLegend}>
            <li className={styles.impactLow}>{t(`consumption:consumptionImpactGraph.legendLow`)}</li>
            <li>{t(`consumption:consumptionImpactGraph.legendNeutral`)}</li>
            <li className={styles.impactHigh}>{t(`consumption:consumptionImpactGraph.legendHigh`)}</li>
          </ul>
          <footer className={styles.impactFooter}>
            {getRanges(getHours('LOW')).length > 0 && (
              <div>
                <h4>{t(`consumption:consumptionImpactGraph.hoursPrefer`)}</h4>
                {getRanges(getHours('LOW')).map((r) => (
                  <React.Fragment key={r}>
                    {r}
                    <br />
                  </React.Fragment>
                ))}
              </div>
            )}
            {getRanges(getHours('HIGH')).length > 0 && (
              <div>
                <h4>{t(`consumption:consumptionImpactGraph.hoursAvoid`)}</h4>
                {getRanges(getHours('HIGH')).map((r) => (
                  <React.Fragment key={r}>
                    {r}
                    <br />
                  </React.Fragment>
                ))}
              </div>
            )}
            <Info>{t(`consumption:consumptionImpactGraph.notice`)}</Info>
          </footer>
        </div>
      )}
    </div>
  );
};

export default ConsumptionImpactGraph;
